import './hint.css';

/**
 * Reusable "?" hint: a small circled question mark that reveals a short
 * explanatory popup. Designed to sit next to a form label.
 *
 * Behaviour:
 *   - Desktop: hovering (or focusing) the icon shows the popup; leaving hides it.
 *   - Touch / click: clicking toggles the popup open; clicking the "×",
 *     clicking outside, or pressing Escape closes it. (Hover does nothing
 *     useful on touch, so the explicit open/close is the primary path.)
 *
 * Usage:
 *   const h = createHint('Servings in one purchasable pack. 0 = can’t be bought.');
 *   label.append(' ', h);
 *
 * @param {string | Node} content  popup text, or a Node for rich content
 * @param {object} [opts]
 * @param {string} [opts.label]     the trigger's aria-label
 * @param {string} [opts.icon]      trigger `fa-*` icon (default the "?" circle)
 * @param {string} [opts.className] extra class on the wrapper (popup variants)
 * Returns the wrapper <span> (inline, so it flows after label text).
 */
export function createHint(
  content,
  { label = 'More information', icon = 'fa-circle-question', className } = {},
) {
  const wrap = document.createElement('span');
  wrap.className = className ? `hint ${className}` : 'hint';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'hint-trigger';
  trigger.setAttribute('aria-label', label);
  // No `title` here: hovering already reveals the hint popup, so a native
  // tooltip would duplicate it (and fight the popup on hover).
  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-expanded', 'false');
  const iconEl = document.createElement('i');
  iconEl.className = `fa-solid ${icon}`;
  iconEl.setAttribute('aria-hidden', 'true');
  trigger.append(iconEl);

  const pop = document.createElement('span');
  pop.className = 'hint-popup';
  pop.setAttribute('role', 'tooltip');
  pop.hidden = true;

  // Plain text, or a pre-built Node for rich content (e.g. rendered notes) —
  // shown as a block, scrolling if it's long.
  const body = document.createElement('span');
  body.className = 'hint-popup-text';
  if (content instanceof Node) {
    body.classList.add('hint-popup-text--rich');
    body.append(content);
  } else {
    body.textContent = content;
  }

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'hint-close';
  close.setAttribute('aria-label', 'Close');
  close.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';

  // Close button first in source so `float: right` lets the text wrap around it
  // starting from the first line.
  pop.append(close, body);

  // `pinned` = opened by click/tap (sticky until dismissed). Hover only shows
  // transiently and never closes a pinned popup.
  let pinned = false;

  function show() {
    pop.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    const bounds = clipBounds();
    placeVertically(bounds);
    clampHorizontally(bounds);
  }

  /**
   * Open above the icon by default, but flip below when there isn't room above
   * inside the clip container (e.g. a hint near the top of a scrolled modal
   * body, which would otherwise hide most of the text).
   */
  function placeVertically(bounds) {
    pop.classList.remove('hint-popup--below');
    const margin = 8;
    const rect = pop.getBoundingClientRect();
    if (rect.top >= bounds.top + margin) return;
    const t = trigger.getBoundingClientRect();
    const roomBelow = bounds.bottom - t.bottom;
    const roomAbove = t.top - bounds.top;
    if (roomBelow > roomAbove) pop.classList.add('hint-popup--below');
  }

  /**
   * Keep the popup inside its container so it never overflows the modal (which
   * would clip the "×" and push out a horizontal scrollbar). We clamp to the
   * nearest scroll/clip container — a dialog/modal or any ancestor with a
   * non-visible overflow — falling back to the viewport. The popup is centered
   * on the trigger (transform: translateX(-50%)); if an edge would spill past an
   * 8px margin we nudge it back via a pixel offset on the transform.
   */
  function clampHorizontally(bounds = clipBounds()) {
    pop.style.transform = 'translateX(-50%)'; // reset before measuring
    const margin = 8;
    const rect = pop.getBoundingClientRect();
    let shift = 0;
    if (rect.right > bounds.right - margin) shift = bounds.right - margin - rect.right;
    else if (rect.left < bounds.left + margin) shift = bounds.left + margin - rect.left;
    if (shift) {
      pop.style.transform = `translateX(calc(-50% + ${Math.round(shift)}px))`;
      // The arrow is centered under the popup; once shifted it no longer points
      // at the icon, so hide it rather than show a misaligned pointer.
      pop.classList.add('hint-popup--shifted');
    } else {
      pop.classList.remove('hint-popup--shifted');
    }
  }

  /** The bounds (in viewport coords) the popup must stay within. */
  function clipBounds() {
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight || window.innerHeight;
    let el = wrap.parentElement;
    while (el) {
      const tag = el.tagName;
      const overflowX = getComputedStyle(el).overflowX;
      if (tag === 'DIALOG' || el.classList.contains('modal') || overflowX !== 'visible') {
        const r = el.getBoundingClientRect();
        // Use the content edge (exclude border + any vertical scrollbar gutter)
        // so the popup never sits under the scrollbar.
        const styles = getComputedStyle(el);
        const borderLeft = parseFloat(styles.borderLeftWidth) || 0;
        const borderRight = parseFloat(styles.borderRightWidth) || 0;
        const gutter = el.offsetWidth - el.clientWidth - borderLeft - borderRight; // scrollbar
        const borderTop = parseFloat(styles.borderTopWidth) || 0;
        const borderBottom = parseFloat(styles.borderBottomWidth) || 0;
        return {
          left: Math.max(0, r.left + borderLeft),
          right: Math.min(vw, r.right - borderRight - Math.max(0, gutter)),
          top: Math.max(0, r.top + borderTop),
          bottom: Math.min(vh, r.bottom - borderBottom),
        };
      }
      el = el.parentElement;
    }
    return { left: 0, right: vw, top: 0, bottom: vh };
  }
  function hide() {
    pinned = false;
    pop.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', onOutsideClick, true);
    document.removeEventListener('keydown', onKeydown, true);
  }
  function pin() {
    pinned = true;
    show();
    document.addEventListener('click', onOutsideClick, true);
    document.addEventListener('keydown', onKeydown, true);
  }
  function onOutsideClick(event) {
    if (!wrap.contains(event.target)) hide();
  }
  function onKeydown(event) {
    if (event.key === 'Escape') {
      hide();
      trigger.focus();
    }
  }

  // Swallow pointer/mouse events too: the hint usually lives inside a <label>,
  // and clicking any descendant of a label activates its control (e.g. toggling
  // a checkbox). stopPropagation on `click` alone doesn't stop that — browsers
  // can initiate label activation on mousedown — so block those as well.
  for (const type of ['mousedown', 'pointerdown', 'mouseup']) {
    trigger.addEventListener(type, (event) => {
      event.stopPropagation();
      event.preventDefault();
    });
  }
  trigger.addEventListener('click', (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (pinned) hide();
    else pin();
  });
  close.addEventListener('click', (event) => {
    event.stopPropagation();
    hide();
  });

  // Transient hover/focus preview (ignored while pinned so it doesn't fight the
  // click-to-close on touch devices that also synthesize hover).
  wrap.addEventListener('mouseenter', () => {
    if (!pinned) show();
  });
  wrap.addEventListener('mouseleave', () => {
    if (!pinned) {
      pop.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    }
  });
  trigger.addEventListener('focus', () => {
    if (!pinned) show();
  });
  trigger.addEventListener('blur', () => {
    if (!pinned) {
      pop.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    }
  });

  wrap.append(trigger, pop);
  return wrap;
}

/**
 * A field caption with a trailing "?" hint, as ONE element — so inside a
 * column-flex `.modal label` the hint stays on the caption's line instead of
 * becoming its own row. The text sits in a `.field-caption-text` span so it can
 * be changed later without disturbing the hint.
 * @param {string} text
 * @param {string} [hintText]  no hint when empty
 */
export function captionWithHint(text, hintText) {
  const caption = document.createElement('span');
  caption.className = 'caption-with-hint';
  const textEl = document.createElement('span');
  textEl.className = 'field-caption-text';
  textEl.textContent = text;
  caption.append(textEl);
  if (hintText) caption.append(' ', createHint(hintText));
  return caption;
}
